/**
 * Which blocks the person at this screen changed (useDocumentBody.js): per
 * block id, the content (stableStringify) last saved from here or taken in
 * from a teammate. A save writes only the blocks that differ from it
 * (docBlocks.planBlockWrites `onlyIds`) — a teammate's paragraph shown here
 * live is theirs to save.
 *
 * `known` is false until the baseline describes what is stored (a load or a
 * full save), and again after an AI/MCP apply replaced the whole body: the
 * next save then compares every block with the store, deletes included.
 */
import { stableStringify } from '../ui/lexicalBlocks';

export function createBaseline(snapshot = null) {
  return {
    map: new Map((snapshot?.blocks || []).map((b) => [b.id, stableStringify(b.node)])),
    // Block order as last saved from here or taken in from a teammate: a block
    // whose place differs from it was MOVED here (dragged), and only those get
    // a new key from this screen — a teammate's move is not undone from here.
    order: (snapshot?.blocks || []).map((b) => b.id),
    known: !!snapshot,
  };
}

/** Ids of blocks moved at this screen since the baseline (the kept ones: longest run in baseline order). */
export function movedSince(baseline, snapshot) {
  if (!snapshot?.blocks) return new Set();
  const index = new Map(baseline.order.map((id, i) => [id, i]));
  const seq = snapshot.blocks.map((b) => b.id).filter((id) => index.has(id));
  const tails = [];
  const prev = new Array(seq.length).fill(-1);
  for (let i = 0; i < seq.length; i += 1) {
    const v = index.get(seq[i]);
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (index.get(seq[tails[mid]]) < v) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) prev[i] = tails[lo - 1];
    tails[lo] = i;
  }
  const kept = new Set();
  for (let i = tails.length ? tails[tails.length - 1] : -1; i !== -1; i = prev[i]) kept.add(seq[i]);
  return new Set(seq.filter((id) => !kept.has(id)));
}

/**
 * `Map<id, json | null>` of the blocks changed here since the baseline (null
 * = deleted here), or null when the baseline is not known (save everything).
 */
export function changedSince(baseline, snapshot) {
  if (!baseline.known || !snapshot?.blocks) return null;
  const changed = new Map();
  const present = new Set();
  for (const b of snapshot.blocks) {
    present.add(b.id);
    const json = stableStringify(b.node);
    if (baseline.map.get(b.id) !== json) changed.set(b.id, json);
  }
  for (const id of baseline.map.keys()) if (!present.has(id)) changed.set(id, null);
  return changed;
}

/** After a save of `changed` (from changedSince): those blocks are now stored as saved. */
export function noteSaved(baseline, changed, snapshot) {
  if (!changed) return createBaseline(snapshot);
  changed.forEach((json, id) => (json == null ? baseline.map.delete(id) : baseline.map.set(id, json)));
  if (snapshot?.blocks) baseline.order = snapshot.blocks.map((b) => b.id);
  return baseline;
}

/**
 * A teammate's edit was applied: `ids` are the blocks the editor replaced
 * (they hold the teammate's version now, or are gone). Without `ids`, every
 * block that differs between `prev` and `next` counts.
 *
 * Order: a live edit carries text, never a move, so a block it touched keeps
 * its place in the order baseline (only new blocks are placed). `place`
 * (a reconcile from stored records, which carry keys) places the touched
 * blocks where they now stand.
 */
export function noteRemote(baseline, next, ids = null, prev = null, { place = false } = {}) {
  const nextById = new Map((next?.blocks || []).map((b) => [b.id, b.node]));
  if (ids) {
    // Only the blocks the teammate's edit placed move in the order baseline;
    // the rest keep their baseline order — a block dragged HERE and not saved
    // yet must still read as moved here.
    const known = new Set(baseline.order);
    const replace = (id) => ids.has(id) && (place || !known.has(id));
    const order = baseline.order.filter((id) => !replace(id) && nextById.has(id));
    const nextIds = (next?.blocks || []).map((b) => b.id);
    nextIds.forEach((id, i) => {
      if (!replace(id)) return;
      let at = 0;
      for (let j = i - 1; j >= 0; j -= 1) {
        const k = order.indexOf(nextIds[j]);
        if (k !== -1) {
          at = k + 1;
          break;
        }
      }
      order.splice(at, 0, id);
    });
    baseline.order = order;
    for (const id of ids) {
      if (nextById.has(id)) baseline.map.set(id, stableStringify(nextById.get(id)));
      else baseline.map.delete(id);
    }
    return baseline;
  }
  if (next?.blocks) baseline.order = next.blocks.map((b) => b.id);
  const before = new Map((prev?.blocks || []).map((b) => [b.id, stableStringify(b.node)]));
  for (const [id, node] of nextById) {
    const json = stableStringify(node);
    if (before.get(id) !== json) baseline.map.set(id, json);
  }
  for (const id of before.keys()) if (!nextById.has(id)) baseline.map.delete(id);
  return baseline;
}
